/**
 * Debate SSE client — typed entry points over the generic runner in
 * `sseStream.ts`.
 *
 * Framing, backoff/jitter, abort semantics and mid-stream recovery live in
 * `sseStream.ts` and are shared with the Keşif crawl stream. What remains
 * here is exactly what is debate-specific: the `DebateEvent` decoder and the
 * two 409 responses that are protocol signals rather than failures
 * (`needs_clarification` on admit, `debate.not_live` on reattach).
 *
 * Invariants: every accepted frame goes through `eventCodec`; an abort never
 * retries; a 409 never retries.
 */
import { decodeEvent, type CodecLogger } from "./eventCodec";
import { runSseStream, type SseReconnectPolicy } from "./sseStream";
import { NeedsClarificationError, type DebateEvent } from "../domain/types";

export type { SseReconnectPolicy };

export interface SseClientOptions {
  url: string;
  body: unknown;
  headers?: Record<string, string>;
  signal: AbortSignal;
  onEvent: (evt: DebateEvent) => void;
  onError?: (err: Error) => void;
  /**
   * Called each time a reconnect attempt is about to be made — lets the UI
   * surface "Yeniden bağlanıyor (2/8)..." banners without blocking the retry.
   */
  onReconnecting?: (attempt: number, maxAttempts: number, delayMs: number) => void;
  reconnect?: SseReconnectPolicy;
  logger?: CodecLogger;
}

export async function openDebateStream(opts: SseClientOptions): Promise<void> {
  await runSseStream<DebateEvent>({
    url: opts.url,
    method: "POST",
    body: opts.body ?? {},
    ...(opts.headers ? { headers: opts.headers } : {}),
    signal: opts.signal,
    decode: decodeEvent,
    onEvent: opts.onEvent,
    ...(opts.onError ? { onError: opts.onError } : {}),
    ...(opts.onReconnecting ? { onReconnecting: opts.onReconnecting } : {}),
    ...(opts.reconnect ? { reconnect: opts.reconnect } : {}),
    ...(opts.logger ? { logger: opts.logger } : {}),
    onResponse: async (res) => {
      // Görev 5 — 409 needs_clarification is NOT a transport failure: the
      // session was never admitted server-side. Surfacing it as a typed
      // error lets the caller show the clarifying questions instead of a
      // generic connection banner. Never retried: resubmitting the IDENTICAL
      // body returns the same 409, so the caller must add
      // `clarification_answers` / `skip_clarification` first.
      if (res.status !== 409) return null;
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        return null; // malformed body — fall back to the default verdict
      }
      if (
        !body ||
        typeof body !== "object" ||
        (body as { status?: unknown }).status !== "needs_clarification"
      ) {
        return null;
      }
      const b = body as { questions?: unknown; frame?: unknown };
      const questions = Array.isArray(b.questions)
        ? b.questions.filter((q): q is string => typeof q === "string")
        : [];
      const frame =
        b.frame && typeof b.frame === "object"
          ? (b.frame as Record<string, unknown>)
          : null;
      return { kind: "fatal", error: new NeedsClarificationError(questions, frame) };
    },
  });
}

export interface SseReattachOptions {
  url: string;
  headers?: Record<string, string>;
  signal: AbortSignal;
  onEvent: (evt: DebateEvent) => void;
  onError?: (err: Error) => void;
  /**
   * HTTP 409 `debate.not_live` — the debate is no longer running in THIS
   * backend process (already finalized/interrupted, or the process restarted
   * since it was running). A distinct, EXPECTED terminal signal: never
   * retried and never routed through `onError`. The caller should fall back
   * to the static `GET /api/debate/history/{id}` replay it already hydrated
   * before calling this.
   */
  onNotLive?: () => void;
  onReconnecting?: (attempt: number, maxAttempts: number, delayMs: number) => void;
  reconnect?: SseReconnectPolicy;
  logger?: CodecLogger;
}

/**
 * İş Kolu — arka planda devam eden tartışma.
 *
 * Re-attach to an ALREADY-LIVE debate via `GET /api/debate/{id}/stream` (tab
 * reopen / reconnect while the engine keeps running server-side regardless of
 * viewer connectivity — see backend `routes_debate.py`).
 *
 * Deliberately simpler than `openDebateStream`: no request body (nothing to
 * admit — the engine already exists) and no `needs_clarification` handling
 * (that gate only fires on the ADMITTING `POST /api/debate` call).
 */
export async function reattachDebateStream(opts: SseReattachOptions): Promise<void> {
  await runSseStream<DebateEvent>({
    url: opts.url,
    method: "GET",
    ...(opts.headers ? { headers: opts.headers } : {}),
    signal: opts.signal,
    decode: decodeEvent,
    onEvent: opts.onEvent,
    ...(opts.onError ? { onError: opts.onError } : {}),
    ...(opts.onReconnecting ? { onReconnecting: opts.onReconnecting } : {}),
    ...(opts.reconnect ? { reconnect: opts.reconnect } : {}),
    ...(opts.logger ? { logger: opts.logger } : {}),
    onResponse: async (res) => {
      if (res.status === 409) {
        opts.onNotLive?.();
        return { kind: "stop" };
      }
      return null;
    },
  });
}
